import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import UserDashboard from './components/Dashboard/UserDashboard';
import TablesPage from './components/Tables/TablesPage';
import TableDetails from './components/Tables/TableDetails';
import UserTableDetails from './components/Tables/UserTableDetails';
import InventorySnapshotPage from './components/Inventory/InventorySnapshotPage';
import EventsDisplayPage from './components/Events/EventsDisplayPage';
import FinancialsPage from './components/Financials/FinancialsPage';
import FinancialsReportsPage from './components/Financials/FinancialsReportsPage';
import PrepurchasedInventoryPage from './components/Financials/PrepurchasedInventoryPage';
import EntityStatementPage from './components/Financials/EntityStatementPage';
import ManualEntryPage from './components/ManualEntry/ManualEntryPage';
import ArtistsPage from './components/Artists/ArtistsPage';
import VenuesPage from './components/Venues/VenuesPage';
import TicketTypesPage from './components/TicketTypes/TicketTypesPage';
import CardsPage from './components/Cards/CardsPage';
import PhonesPage from './components/Phones/PhonesPage';
import ContactsPage from './components/Contacts/ContactsPage';
import EmailsPage from './components/Emails/EmailsPage';
import AccountsPage from './components/Accounts/AccountsPage';
import PricingHistoryPage from './components/Transactions/PricingHistoryPage';
import TransactionsPage from './components/Transactions/TransactionsPage';
import { authService } from './services/api';
import { getApiUserCode } from './components/Shared/ApiUserBadge';
import './App.css';

const THEME_STORAGE_KEY = 'tikwiz-page-theme';

const getInitialTheme = () => {
  try {
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return savedTheme === 'light' ? 'light' : 'dark';
  } catch (error) {
    return 'dark';
  }
};

const ThemeToggleIcon = ({ theme }) => {
  if (theme === 'light') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2" />
        <path d="M12 20v2" />
        <path d="m4.93 4.93 1.41 1.41" />
        <path d="m17.66 17.66 1.41 1.41" />
        <path d="M2 12h2" />
        <path d="M20 12h2" />
        <path d="m6.34 17.66-1.41 1.41" />
        <path d="m19.07 4.93-1.41 1.41" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5 8.5 8.5 0 1 0 20.5 14.2Z" />
    </svg>
  );
};

const RootRoute = () => {
  const token = authService.getToken();
  const [status, setStatus] = useState('checking');
  const [targetPath, setTargetPath] = useState(null);

  useEffect(() => {
    let isMounted = true;

    if (!token) {
      setStatus('login');
      return;
    }

    authService.getCurrentUser(token)
      .then((user) => {
        if (!isMounted) return;
        setTargetPath(authService.isAdmin(user) ? '/dashboard' : '/user');
        setStatus('redirect');
      })
      .catch(() => {
        if (!isMounted) return;
        authService.logout();
        setStatus('login');
      });

    return () => {
      isMounted = false;
    };
  }, [token]);

  if (status === 'checking') {
    return (
      <div className="route-loading">
        <div className="loading-spinner"></div>
        <p>Checking access...</p>
      </div>
    );
  }

  if (status === 'redirect' && targetPath) {
    return <Navigate to={targetPath} replace />;
  }

  return <LoginForm />;
};

// Protected Route component
const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireRegular = false,
  allowedApiUserCodes = null,
  forbiddenRedirect = null,
}) => {
  const token = authService.getToken();
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const allowedApiUserCodeKey = Array.isArray(allowedApiUserCodes) ? allowedApiUserCodes.join('|') : '';

  const redirectTarget = useMemo(() => {
    if (forbiddenRedirect) {
      return forbiddenRedirect;
    }
    if (requireAdmin) {
      return '/user';
    }
    if (requireRegular) {
      return '/dashboard';
    }
    return '/';
  }, [forbiddenRedirect, requireAdmin, requireRegular]);

  useEffect(() => {
    let isMounted = true;

    if (!token) {
      setStatus('unauthorized');
      return;
    }

    authService.getCurrentUser(token)
      .then((user) => {
        if (!isMounted) return;
        const allowedCodes = allowedApiUserCodeKey ? allowedApiUserCodeKey.split('|') : null;

        if (requireAdmin && !authService.isAdmin(user)) {
          setStatus('forbidden');
          return;
        }

        if (requireRegular && authService.isAdmin(user)) {
          setStatus('forbidden');
          return;
        }

        if (allowedCodes && !allowedCodes.includes(getApiUserCode(user.email))) {
          setStatus('forbidden');
          return;
        }

        setStatus('authorized');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('unauthorized');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [token, requireAdmin, requireRegular, allowedApiUserCodeKey]);

  if (status === 'loading') {
    return (
      <div className="route-loading">
        <div className="loading-spinner"></div>
        <p>Checking access...</p>
      </div>
    );
  }

  if (status === 'unauthorized') {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (status === 'forbidden') {
    return <Navigate to={redirectTarget} replace />;
  }

  return children;
};

function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const nextThemeLabel = nextTheme === 'light' ? 'Light' : 'Dark';

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.body.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (error) {
      // The visual theme still changes if storage is unavailable.
    }
  }, [theme]);

  return (
    <div className="App" data-theme={theme}>
      <Router>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireAdmin>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tables"
            element={
              <ProtectedRoute requireAdmin>
                <TablesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tables/:tableName"
            element={
              <ProtectedRoute requireAdmin>
                <TableDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/inventory"
            element={
              <ProtectedRoute requireAdmin>
                <InventorySnapshotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/events"
            element={
              <ProtectedRoute requireAdmin>
                <EventsDisplayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/financials"
            element={
              <ProtectedRoute requireAdmin>
                <FinancialsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/financials/reports"
            element={
              <ProtectedRoute requireAdmin>
                <FinancialsReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/financials/reports/prepurchased-inventory"
            element={
              <ProtectedRoute requireAdmin>
                <PrepurchasedInventoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/financials/reports/entity-statement"
            element={
              <ProtectedRoute requireAdmin>
                <EntityStatementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/manual-entry"
            element={
              <ProtectedRoute requireAdmin>
                <ManualEntryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user"
            element={
              <ProtectedRoute requireRegular>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/events"
            element={
              <ProtectedRoute requireRegular>
                <EventsDisplayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/financials"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user">
                <FinancialsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/financials/reports"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user">
                <FinancialsReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/financials/reports/prepurchased-inventory"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user">
                <PrepurchasedInventoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/financials/reports/entity-statement"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user">
                <EntityStatementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/manual-entry"
            element={
              <ProtectedRoute requireRegular>
                <ManualEntryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/info"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/info/artists" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/accounts"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/accounts/dice" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/accounts/:vendorSlug"
            element={
              <ProtectedRoute requireRegular>
                <AccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/transactions"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/transactions/orders" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/transactions/orders/pricing-history"
            element={<ProtectedRoute><PricingHistoryPage /></ProtectedRoute>}
          />
          <Route
            path="/user/transactions/orders"
            element={
              <ProtectedRoute requireRegular>
                <TransactionsPage forcedType="orders" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/transactions/payments"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user/transactions/orders">
                <TransactionsPage forcedType="payments" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/transactions/:transactionType"
            element={
              <ProtectedRoute requireRegular>
                <TransactionsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/user/info/contacts" element={<ProtectedRoute requireRegular><ContactsPage /></ProtectedRoute>} />
          <Route
            path="/user/info/emails"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user/info/artists">
                <EmailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/info/artists"
            element={
              <ProtectedRoute requireRegular>
                <ArtistsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/info/venues"
            element={
              <ProtectedRoute requireRegular>
                <VenuesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/info/ticket-types"
            element={
              <ProtectedRoute requireRegular>
                <TicketTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/info/cards"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user/info/artists">
                <CardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/info/phones"
            element={
              <ProtectedRoute requireRegular allowedApiUserCodes={['TS', 'DV']} forbiddenRedirect="/user/info/artists">
                <PhonesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/artists"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/info/artists" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/venues"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/info/venues" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/emails"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/info/emails" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/cards"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/info/cards" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/phones"
            element={
              <ProtectedRoute requireRegular>
                <Navigate to="/user/info/phones" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/tables/:tableName"
            element={
              <ProtectedRoute requireRegular>
                <UserTableDetails />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <button
        type="button"
        className="theme-toggle-button"
        onClick={() => setTheme(nextTheme)}
        aria-label={`Switch to ${nextTheme} theme`}
        title={`Switch to ${nextTheme} theme`}
      >
        <span className="theme-toggle-icon">
          <ThemeToggleIcon theme={nextTheme} />
        </span>
        <span className="theme-toggle-text">{nextThemeLabel}</span>
      </button>
    </div>
  );
}

export default App;
